import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { registerApiToastHandler } from '../api';

const ToastContext = createContext(null);

let toastId = 0;

const TYPE_CONFIG = {
  success: {
    icon: CheckCircle,
    border: 'border-accent-green/40',
    text: 'text-accent-green',
    bar: 'bg-accent-green',
    duration: 4000,
  },
  error: {
    icon: AlertCircle,
    border: 'border-accent-red/40',
    text: 'text-accent-red',
    bar: 'bg-accent-red',
    duration: 6000,
  },
  warning: {
    icon: AlertTriangle,
    border: 'border-yellow-500/40',
    text: 'text-yellow-400',
    bar: 'bg-yellow-400',
    duration: 5000,
  },
  info: {
    icon: Info,
    border: 'border-accent-blue/40',
    text: 'text-accent-blue',
    bar: 'bg-accent-blue',
    duration: 4000,
  },
};

function ToastItem({ toast, onClose }) {
  const cfg = TYPE_CONFIG[toast.type] || TYPE_CONFIG.info;
  const Icon = cfg.icon;
  const [progress, setProgress] = useState(100);
  const duration = toast.duration ?? cfg.duration;

  useEffect(() => {
    if (!duration || duration <= 0) return undefined;
    const start = Date.now();
    const tick = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(pct);
      if (pct <= 0) clearInterval(tick);
    }, 50);
    return () => clearInterval(tick);
  }, [duration]);

  return (
    <div
      role="status"
      aria-live="polite"
      className={`relative flex items-start gap-3 px-4 py-3 rounded-lg border shadow-lg animate-slide-in bg-navy-800 ${cfg.border} ${cfg.text} overflow-hidden min-w-[280px]`}
    >
      <Icon size={18} className="flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        {toast.title && <div className="text-sm font-semibold text-white mb-0.5">{toast.title}</div>}
        <div className="text-sm text-white break-words">{toast.message}</div>
      </div>
      <button
        onClick={onClose}
        className="text-slate-400 hover:text-white flex-shrink-0 transition-colors"
        aria-label="Dismiss notification"
      >
        <X size={14} />
      </button>
      {duration > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-navy-900/40">
          <div
            className={`h-full ${cfg.bar} transition-all ease-linear`}
            style={{ width: `${progress}%`, transitionDuration: '50ms' }}
          />
        </div>
      )}
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback((message, type = 'info', duration) => {
    const id = ++toastId;
    let opts = { message };
    if (typeof message === 'object' && message !== null) {
      opts = message;
    }
    const cfgDur = TYPE_CONFIG[type]?.duration ?? 4000;
    const finalDuration = duration ?? opts.duration ?? cfgDur;
    setToasts(prev => {
      // Cap at 5 stacked toasts — drop oldest
      const next = [...prev, { id, message: opts.message, title: opts.title, type, duration: finalDuration }];
      return next.length > 5 ? next.slice(next.length - 5) : next;
    });
    if (finalDuration > 0) {
      setTimeout(() => removeToast(id), finalDuration);
    }
    return id;
  }, [removeToast]);

  const success = useCallback((msg, opts = {}) => addToast(typeof msg === 'string' ? { message: msg, ...opts } : msg, 'success'), [addToast]);
  const error = useCallback((msg, opts = {}) => addToast(typeof msg === 'string' ? { message: msg, ...opts } : msg, 'error'), [addToast]);
  const warning = useCallback((msg, opts = {}) => addToast(typeof msg === 'string' ? { message: msg, ...opts } : msg, 'warning'), [addToast]);
  const info = useCallback((msg, opts = {}) => addToast(typeof msg === 'string' ? { message: msg, ...opts } : msg, 'info'), [addToast]);

  // Register with api.js so axios interceptors can fire toasts globally
  useEffect(() => {
    registerApiToastHandler({ success, error, warning, info });
  }, [success, error, warning, info]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast, success, error, warning, info }}>
      {children}
      <div
        className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 max-w-sm pointer-events-none"
        data-testid="toast-container"
      >
        {toasts.map(t => (
          <div key={t.id} className="pointer-events-auto">
            <ToastItem toast={t} onClose={() => removeToast(t.id)} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be inside ToastProvider');
  return ctx;
}
